import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  Board,
  Move,
  IMove,
  IPiece,
  Position,
  PieceType,
} from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 11: Two Turn King Defence
 *
 * ENHANCED: 4-ply simulation to defend against 2-turn king threats
 *
 * Analysis:
 * 1. Find all enemy king 2-turn winning paths
 * 2. For each path, simulate our defensive responses
 * 3. CRITICAL: Defended pieces are SAFE from king capture (kingslayer suicide)
 * 4. 4-ply: Enemy King → Our Defense → Enemy King → Result
 */
export class TwoTurnKingDefencePriority extends BaseDecisionPriority {
  readonly priority = 12;
  readonly name = "Two Turn King Defence";
  readonly description =
    "ENHANCED: 4-ply defense simulation with defended piece logic";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const enemyColor = isPlayerTurn ? "black" : "white";

    // Find our king
    const ourKing = this.findOurKing(gameState, isPlayerTurn);
    if (!ourKing) {
      this.addLog("❌ No king found on board");
      return {
        shouldEarlyExit: false,
        reasoning: "No king found",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // STEP 1: Find all enemy king 2-turn winning paths
    const enemyTwoTurnThreats = this.findEnemyTwoTurnThreats(
      gameState,
      enemyColor
    );

    if (enemyTwoTurnThreats.length === 0) {
      this.addLog("✅ No enemy 2-turn king threats detected");
      return {
        shouldEarlyExit: false,
        reasoning: "No 2-turn king threats",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(
      `🚨 ENEMY 2-TURN THREATS: Found ${enemyTwoTurnThreats.length} winning paths`
    );

    // STEP 2: Find defensive moves that block ALL 2-turn threats
    const defensiveMoves = this.findDefensiveMovesAgainstTwoTurnThreats(
      context.allPossibleMoves,
      gameState,
      enemyTwoTurnThreats,
      enemyColor
    );

    if (defensiveMoves.length === 0) {
      this.addLog("💀 NO DEFENSE: No moves can prevent 2-turn king threats");
      return {
        shouldEarlyExit: false,
        reasoning: "No defense against 2-turn king threats",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // CRITICAL DEFENSE FOUND - Early exit
    this.addLog(
      `🛡️ CRITICAL DEFENSE: ${defensiveMoves.length} moves prevent 2-turn threats`
    );

    if (defensiveMoves.length === 1) {
      return this.createEarlyExitResult(
        defensiveMoves[0],
        `${this.name}: CRITICAL - Only defense against 2-turn king threats`,
        {
          twoTurnDefense: true,
          threatsFound: enemyTwoTurnThreats.length,
          defensesFound: defensiveMoves.length,
          logs: this.analysisLogs,
        }
      );
    } else {
      // CRITICAL FIX: Multiple defenses found - pick the best one for early exit
      // Prioritize king moves first (king safety), then closest to threats
      const bestDefense = this.selectBestDefensiveMove(
        defensiveMoves,
        gameState
      );

      this.addLog(
        `🛡️ BEST DEFENSE: Selected [${bestDefense.fromPosition.row},${bestDefense.fromPosition.col}] → [${bestDefense.move.position.row},${bestDefense.move.position.col}] from ${defensiveMoves.length} options`
      );

      return this.createEarlyExitResult(
        bestDefense,
        `${this.name}: CRITICAL - Best defense against 2-turn king threats`,
        {
          twoTurnDefense: true,
          threatsFound: enemyTwoTurnThreats.length,
          defensesFound: defensiveMoves.length,
          logs: this.analysisLogs,
        }
      );
    }
  }

  /**
   * Find all enemy king 2-turn winning paths
   * Returns: Array of {firstMove, secondMove} threat sequences
   */
  private findEnemyTwoTurnThreats(
    gameState: any,
    enemyColor: "white" | "black"
  ): Array<{ firstMove: MoveInfo; secondMove: MoveInfo }> {
    const threats: Array<{ firstMove: MoveInfo; secondMove: MoveInfo }> = [];
    const victoryRow = enemyColor === "white" ? 7 : 0;

    // Find enemy king
    const enemyKing = this.findKing(gameState.board, enemyColor);
    if (!enemyKing) return threats;

    this.addLog(
      `👑 Analyzing enemy ${enemyColor} king at [${enemyKing.row},${enemyKing.col}]`
    );

    // Generate enemy king's first moves
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyFirstMoves = moveService
        .getAllMoves(gameState)
        .filter(
          (move) =>
            move.fromPosition.row === enemyKing.row &&
            move.fromPosition.col === enemyKing.col
        );

      this.addLog(
        `🔍 Enemy king has ${enemyFirstMoves.length} first moves to analyze`
      );

      // For each first move, check if second move can win
      for (const firstMove of enemyFirstMoves) {
        // Skip if first move already wins (that's 1-turn, not 2-turn)
        if (firstMove.move.position.row === victoryRow) continue;

        // CRITICAL FIX: Skip moves to defended squares - king won't suicide!
        if (
          this.isSquareDefendedAgainstKing(
            gameState,
            firstMove.move.position,
            enemyColor
          )
        ) {
          this.addLog(
            `⚡ SKIPPED FAKE THREAT: King won't move to defended [${firstMove.move.position.row},${firstMove.move.position.col}] - kingslayer suicide!`
          );
          continue;
        }

        // Simulate first move
        const stateAfterFirst = this.simulateMove(gameState, firstMove);
        if (!stateAfterFirst) continue;

        // Our turn would be skipped in this simulation - go straight to enemy's second move
        stateAfterFirst.isLightTurn = enemyColor === "white";

        // Generate enemy's second moves from new position
        const enemySecondMoves = moveService
          .getAllMoves(stateAfterFirst)
          .filter(
            (move) =>
              move.fromPosition.row === firstMove.move.position.row &&
              move.fromPosition.col === firstMove.move.position.col
          );

        // Check if any second move wins
        for (const secondMove of enemySecondMoves) {
          if (secondMove.move.position.row === victoryRow) {
            // VICTORY SQUARE FIX: Victory squares are NEVER defended - game ends immediately!
            // Don't check isSquareDefendedAgainstKing for victory squares - reaching them wins the game
            threats.push({ firstMove, secondMove });
            this.addLog(
              `🚨 2-TURN THREAT: [${enemyKing.row},${enemyKing.col}] → [${firstMove.move.position.row},${firstMove.move.position.col}] → [${secondMove.move.position.row},${secondMove.move.position.col}] WINS`
            );
          }
        }
      }
    } finally {
      gameState.isLightTurn = originalTurn;
    }

    return threats;
  }

  /**
   * Check if a move results in hanging the piece
   * CRITICAL: Defensive moves should NEVER hang pieces
   */
  private isMoveHanging(moveInfo: MoveInfo, gameState: any): boolean {
    try {
      // Simulate our move
      const stateAfterMove = this.simulateMove(gameState, moveInfo);
      if (!stateAfterMove) return true; // Assume hanging if simulation fails

      // Switch to enemy turn
      const enemyColor = gameState.isLightTurn ? "black" : "white";
      stateAfterMove.isLightTurn = enemyColor === "white";

      // Check if enemy can capture our piece at the new position
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyMoves = moveService.getAllMoves(stateAfterMove);

      for (const enemyMove of enemyMoves) {
        if (
          enemyMove.move.position.row === moveInfo.move.position.row &&
          enemyMove.move.position.col === moveInfo.move.position.col
        ) {
          // Enemy can capture our piece - check if we can recapture
          const stateAfterCapture = this.simulateMove(
            stateAfterMove,
            enemyMove
          );
          if (!stateAfterCapture) continue;

          // Switch back to our turn
          stateAfterCapture.isLightTurn = !(enemyColor === "white");

          // Check if we can recapture
          const ourRecaptureMoves = moveService.getAllMoves(stateAfterCapture);
          let canRecapture = false;

          for (const recaptureMove of ourRecaptureMoves) {
            if (
              recaptureMove.move.position.row === moveInfo.move.position.row &&
              recaptureMove.move.position.col === moveInfo.move.position.col
            ) {
              canRecapture = true;
              break;
            }
          }

          if (!canRecapture) {
            // Enemy can capture and we can't recapture = hanging!
            return true;
          }
        }
      }

      return false; // No hanging found
    } catch (error) {
      return true; // Assume hanging if analysis fails
    }
  }

  /**
   * Select the best defensive move from multiple options
   * Prioritizes: 1) King moves (safety), 2) Moves closer to threats, 3) Central positioning
   */
  private selectBestDefensiveMove(
    defensiveMoves: MoveInfo[],
    gameState: any
  ): MoveInfo {
    // Find our king to prioritize king moves
    const ourKing = this.findOurKing(gameState, gameState.isLightTurn);

    // Separate king moves from other moves
    const kingMoves = defensiveMoves.filter(
      (move) =>
        ourKing &&
        move.fromPosition.row === ourKing.position.row &&
        move.fromPosition.col === ourKing.position.col
    );

    const otherMoves = defensiveMoves.filter(
      (move) =>
        !ourKing ||
        move.fromPosition.row !== ourKing.position.row ||
        move.fromPosition.col !== ourKing.position.col
    );

    // Prioritize king moves first (king safety is critical)
    if (kingMoves.length > 0) {
      this.addLog(
        `🛡️ KING PRIORITY: Choosing from ${kingMoves.length} king defensive moves`
      );
      return kingMoves[0]; // Return first king move found
    }

    // If no king moves, return first other defensive move
    this.addLog(
      `🛡️ NON-KING DEFENSE: No king moves available, using piece move`
    );
    return otherMoves[0];
  }

  /**
   * Find defensive moves that prevent ALL 2-turn threats
   * Uses 4-ply simulation with defended piece logic
   */
  private findDefensiveMovesAgainstTwoTurnThreats(
    allMoves: MoveInfo[],
    gameState: any,
    threats: Array<{ firstMove: MoveInfo; secondMove: MoveInfo }>,
    enemyColor: "white" | "black"
  ): MoveInfo[] {
    const defensiveMoves: MoveInfo[] = [];

    this.addLog(
      `🛡️ Testing ${allMoves.length} moves for 2-turn defense capability`
    );

    for (const ourMove of allMoves) {
      if (
        this.canMoveDefendAgainstAllThreats(
          ourMove,
          gameState,
          threats,
          enemyColor
        )
      ) {
        // CRITICAL FIX: Check if defensive move is hanging before accepting it
        if (this.isMoveHanging(ourMove, gameState)) {
          this.addLog(
            `❌ HANGING DEFENSE: [${ourMove.fromPosition.row},${ourMove.fromPosition.col}] → [${ourMove.move.position.row},${ourMove.move.position.col}] defends but hangs piece - REJECTED`
          );
          continue; // Skip hanging defensive moves
        }

        defensiveMoves.push(ourMove);
        this.addLog(
          `✅ SAFE DEFENSE: [${ourMove.fromPosition.row},${ourMove.fromPosition.col}] → [${ourMove.move.position.row},${ourMove.move.position.col}] blocks all 2-turn threats`
        );
      }
    }

    return defensiveMoves;
  }

  /**
   * 4-PLY SIMULATION: Check if our move defends against all enemy 2-turn threats
   * ENHANCED: Also checks if our move creates NEW threats by abandoning defensive positions
   */
  private canMoveDefendAgainstAllThreats(
    ourMove: MoveInfo,
    gameState: any,
    threats: Array<{ firstMove: MoveInfo; secondMove: MoveInfo }>,
    enemyColor: "white" | "black"
  ): boolean {
    // Simulate our defensive move
    const stateAfterOurMove = this.simulateMove(gameState, ourMove);
    if (!stateAfterOurMove) return false;

    // CRITICAL NEW CHECK: Does our move create NEW threats by abandoning defense?
    const newThreats = this.findEnemyTwoTurnThreats(
      stateAfterOurMove,
      enemyColor
    );
    if (newThreats.length > threats.length) {
      this.addLog(
        `❌ DEFENSIVE ABANDONMENT: Move [${ourMove.fromPosition.row},${
          ourMove.fromPosition.col
        }] → [${ourMove.move.position.row},${
          ourMove.move.position.col
        }] creates ${
          newThreats.length - threats.length
        } new threats by abandoning defensive positions`
      );
      return false; // Move creates new threats = not defensive
    }

    // Test against each existing threat sequence
    for (const threat of threats) {
      if (!this.doesOurMoveBlockThreat(stateAfterOurMove, threat, enemyColor)) {
        return false; // This move doesn't block this threat
      }
    }

    return true; // Our move blocks ALL threats AND doesn't create new ones
  }

  /**
   * Check if our move blocks a specific 2-turn threat
   * FIXED: Check for defended blocking BEFORE simulating enemy moves
   */
  private doesOurMoveBlockThreat(
    stateAfterOurMove: any,
    threat: { firstMove: MoveInfo; secondMove: MoveInfo },
    enemyColor: "white" | "black"
  ): boolean {
    const victoryRow = enemyColor === "white" ? 7 : 0;

    // CRITICAL FIX: Check if our move creates a threat that ACTUALLY prevents the race
    // Only consider it defensive if the king cannot escape by continuing the race sequence
    const enemyKingPos = this.findKing(stateAfterOurMove.board, enemyColor);
    if (
      enemyKingPos &&
      this.doesOurMovePreventRaceSequence(
        stateAfterOurMove,
        threat,
        enemyKingPos,
        enemyColor
      )
    ) {
      this.addLog(
        `🛡️ RACE-BLOCKING THREAT: Our move creates inescapable threat - king cannot continue race sequence`
      );
      return true; // King cannot continue race while escaping = threat blocked
    }

    // CRITICAL FIX: Check if our piece blocks the first move BEFORE simulating
    const ourPieceAtFirstPos = this.getPieceAt(
      stateAfterOurMove.board,
      threat.firstMove.move.position
    );
    if (
      ourPieceAtFirstPos &&
      ourPieceAtFirstPos.isLight !== (enemyColor === "white")
    ) {
      // Our piece is blocking enemy's first move position
      // CRITICAL: Check if our piece is defended
      if (
        this.isPieceDefendedAt(
          stateAfterOurMove,
          threat.firstMove.move.position,
          ourPieceAtFirstPos.isLight
        )
      ) {
        this.addLog(
          `🛡️ DEFENDED BLOCK: Our piece at [${threat.firstMove.move.position.row},${threat.firstMove.move.position.col}] blocks enemy first move - king capture = kingslayer!`
        );
        return true; // King won't capture defended piece = threat blocked
      }
    }

    // Switch to enemy turn for simulation
    stateAfterOurMove.isLightTurn = enemyColor === "white";

    // Try to simulate enemy's first move (may fail if our piece blocks)
    const stateAfterEnemyFirst = this.simulateMove(
      stateAfterOurMove,
      threat.firstMove
    );
    if (!stateAfterEnemyFirst) return true; // Enemy can't make first move = blocked

    // CRITICAL NEW CHECK: After enemy's first move, can we capture the king?
    const firstMoveTestState = cloneFullGameState(stateAfterEnemyFirst);
    firstMoveTestState.isLightTurn = !(enemyColor === "white"); // Switch to our turn

    const moveService = MCTSMoveGenerationService.getInstance();
    const ourFirstCounterMoves = moveService.getAllMoves(firstMoveTestState);

    // Check if we can capture the king after its first move
    for (const counterMove of ourFirstCounterMoves) {
      if (
        counterMove.move.position.row === threat.firstMove.move.position.row &&
        counterMove.move.position.col === threat.firstMove.move.position.col
      ) {
        this.addLog(
          `🛡️ POST-MOVE CAPTURE: We can capture king at [${threat.firstMove.move.position.row},${threat.firstMove.move.position.col}] after its first move!`
        );
        return true; // We can capture king after first move = threat blocked!
      }
    }

    // CRITICAL FIX: Check if our piece blocks the second move BEFORE simulating
    const ourPieceAtSecondPos = this.getPieceAt(
      stateAfterEnemyFirst.board,
      threat.secondMove.move.position
    );
    if (
      ourPieceAtSecondPos &&
      ourPieceAtSecondPos.isLight !== (enemyColor === "white")
    ) {
      // Our piece is blocking enemy's second move position
      // CRITICAL: Check if our piece is defended
      if (
        this.isPieceDefendedAt(
          stateAfterEnemyFirst,
          threat.secondMove.move.position,
          ourPieceAtSecondPos.isLight
        )
      ) {
        this.addLog(
          `🛡️ DEFENDED BLOCK: Our piece at [${threat.secondMove.move.position.row},${threat.secondMove.move.position.col}] blocks enemy second move - king capture = kingslayer!`
        );
        return true; // King won't capture defended piece = threat blocked
      }
    }

    // If enemy first move succeeds, try second move
    stateAfterEnemyFirst.isLightTurn = enemyColor === "white";

    // Try to make enemy's second move
    const finalState = this.simulateMove(
      stateAfterEnemyFirst,
      threat.secondMove
    );
    if (!finalState) return true; // Enemy can't make second move = blocked

    // FIXED: Race victories are immediate game-ending moves!
    // If enemy king reaches victory row, the game ends immediately - no counter-moves possible
    if (threat.secondMove.move.position.row === victoryRow) {
      this.addLog(
        `💀 RACE VICTORY THREAT: Enemy king reaches victory square [${threat.secondMove.move.position.row},${threat.secondMove.move.position.col}] - game ends immediately!`
      );
      return false; // Cannot block a race victory - game ends when king reaches victory row
    }

    // For non-victory squares, check if we can capture the king after it moves
    finalState.isLightTurn = !(enemyColor === "white"); // Switch to our turn
    const ourCounterMoves = moveService.getAllMoves(finalState);

    // Check if we can capture the king at its new position
    for (const counterMove of ourCounterMoves) {
      if (
        counterMove.move.position.row === threat.secondMove.move.position.row &&
        counterMove.move.position.col === threat.secondMove.move.position.col
      ) {
        this.addLog(
          `🛡️ POST-MOVE CAPTURE: We can capture king at [${threat.secondMove.move.position.row},${threat.secondMove.move.position.col}] after its second move!`
        );
        return true; // We can capture king = threat blocked!
      }
    }

    // If we reach here, the threat is real and we cannot defend against it
    return false;
  }

  /**
   * CRITICAL NEW METHOD: Check if a square is defended against enemy king
   * Used to filter out "fake threats" where king would never move due to defense
   */
  private isSquareDefendedAgainstKing(
    gameState: any,
    position: Position,
    enemyColor: "white" | "black"
  ): boolean {
    try {
      // Check if there's already one of our pieces there
      const existingPiece = this.getPieceAt(gameState.board, position);
      if (existingPiece && existingPiece.isLight !== (enemyColor === "white")) {
        // Our piece is already there - check if it's defended
        return this.isPieceDefendedAt(
          gameState,
          position,
          existingPiece.isLight
        );
      }

      // Empty square - simulate enemy king moving there
      const testState = cloneFullGameState(gameState);

      // Find enemy king
      const enemyKing = this.findKing(testState.board, enemyColor);
      if (!enemyKing) return false;

      // Simulate king moving to the position
      const kingPiece = this.getPieceAt(testState.board, enemyKing);
      if (!kingPiece) return false;

      // Move king to position
      testState.board[enemyKing.row][enemyKing.col].Pieces = [];
      testState.board[position.row][position.col].Pieces = [kingPiece];

      // Switch to our turn
      testState.isLightTurn = !(enemyColor === "white");

      // Check if we can capture the king at this position
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(testState);

      for (const move of ourMoves) {
        if (
          move.move.position.row === position.row &&
          move.move.position.col === position.col
        ) {
          // We can capture the king = defended square!
          return true;
        }
      }

      return false; // Square not defended
    } catch (error) {
      return false; // Assume not defended if simulation fails
    }
  }

  /**
   * SOPHISTICATED: Check if our move creates a threat that ACTUALLY prevents the race sequence
   * Only return true if the king cannot escape our threat by continuing the race
   */
  private doesOurMovePreventRaceSequence(
    gameState: any,
    threat: { firstMove: MoveInfo; secondMove: MoveInfo },
    enemyKingPos: Position,
    enemyColor: "white" | "black"
  ): boolean {
    try {
      // First check: Do we threaten the king at current position?
      gameState.isLightTurn = !(enemyColor === "white");
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(gameState);

      let threatensCurrent = false;
      for (const move of ourMoves) {
        if (
          move.move.position.row === enemyKingPos.row &&
          move.move.position.col === enemyKingPos.col
        ) {
          threatensCurrent = true;
          break;
        }
      }

      if (!threatensCurrent) {
        return false; // We don't even threaten the king
      }

      // Second check: Can the king escape by making the first race move?
      // Simulate the king making the first move of the race sequence
      const testState = cloneFullGameState(gameState);
      testState.isLightTurn = enemyColor === "white";

      const kingEscapeState = this.simulateMove(testState, threat.firstMove);
      if (!kingEscapeState) {
        return true; // King can't make first move = our move blocks the race
      }

      // Third check: After king's first move, do we still threaten it?
      kingEscapeState.isLightTurn = !(enemyColor === "white");
      const ourMovesAfterEscape = moveService.getAllMoves(kingEscapeState);

      const kingNewPos = threat.firstMove.move.position;
      for (const move of ourMovesAfterEscape) {
        if (
          move.move.position.row === kingNewPos.row &&
          move.move.position.col === kingNewPos.col
        ) {
          // We still threaten the king after it moves = race blocked!
          return true;
        }
      }

      // King successfully escaped our threat by continuing race = not a real defense
      return false;
    } catch (error) {
      return false; // Assume not defensive if simulation fails
    }
  }

  /**
   * CRITICAL: Check if a piece at given position is defended by our forces
   * If defended, enemy king capturing it = immediate kingslayer opportunity
   */
  private isPieceDefendedAt(
    gameState: any,
    position: Position,
    isOurPieceLight: boolean
  ): boolean {
    try {
      // Simulate enemy king capturing our piece at this position
      const testState = cloneFullGameState(gameState);

      // Find enemy king
      const enemyColor = isOurPieceLight ? "black" : "white";
      const enemyKing = this.findKing(testState.board, enemyColor);
      if (!enemyKing) return false;

      // Simulate king capturing our piece
      const kingPiece = this.getPieceAt(testState.board, enemyKing);
      if (!kingPiece) return false;

      // Move king to capture position
      testState.board[enemyKing.row][enemyKing.col].Pieces = [];
      testState.board[position.row][position.col].Pieces = [kingPiece];

      // Switch to our turn
      testState.isLightTurn = isOurPieceLight;

      // Check if we can capture the king at the position
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(testState);

      for (const move of ourMoves) {
        if (
          move.move.position.row === position.row &&
          move.move.position.col === position.col
        ) {
          // We can recapture the king = defended piece!
          return true;
        }
      }

      return false; // We cannot recapture = undefended
    } catch (error) {
      return false; // Assume undefended if simulation fails
    }
  }

  /**
   * Simulate a move and return new game state
   */
  private simulateMove(gameState: any, moveInfo: MoveInfo): any | null {
    try {
      const testState = cloneFullGameState(gameState);
      const piece = this.getPieceAt(testState.board, moveInfo.fromPosition);
      if (!piece) return null;

      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      return testState;
    } catch (error) {
      return null;
    }
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private findOurKing(
    gameState: any,
    isLightTurn: boolean
  ): { piece: IPiece; position: Position } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isKing && piece.isLight === isLightTurn) {
          return { piece, position: { row, col } };
        }
      }
    }
    return null;
  }

  private findKing(board: any[][], color: "white" | "black"): Position | null {
    const isLight = color === "white";

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          if (piece.isLight === isLight && piece.isKing) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  private getPieceAt(board: any[][], position: Position): IPiece | null {
    if (
      position.row < 0 ||
      position.row >= board.length ||
      position.col < 0 ||
      position.col >= board[position.row].length
    ) {
      return null;
    }

    const square = board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    return square.Pieces[0];
  }
}

